import { memo, useCallback } from 'react';
import clsx from 'clsx';
import { ICON_SIZE_MD, ICON_STROKE_WIDTH_DEFAULT } from '@/constants/ui';
import type {
  CollapsibleTabsProps,
  CollapsibleTabItem,
} from './CollapsibleTabs.types';
import { useFitsExpanded } from './hooks/useFitsExpanded';
import styles from './CollapsibleTabs.module.css';

/** Icon + collapsible label, shared by the visible tab and the measurement twin. */
function TabContent({ item }: { item: CollapsibleTabItem }) {
  const Icon = item.icon;
  return (
    <>
      <Icon
        size={ICON_SIZE_MD}
        strokeWidth={ICON_STROKE_WIDTH_DEFAULT}
        className={styles.tabIcon}
      />
      <div className={styles.tabLabel}>
        <span className={styles.tabLabelText}>{item.label}</span>
      </div>
      {item.indicator && <span className={styles.tabIndicator} aria-hidden />}
    </>
  );
}

const TabButton = memo(function TabButton({
  item,
  isActive,
  onClick,
}: {
  item: CollapsibleTabItem;
  isActive: boolean;
  onClick: (id: string) => void;
}) {
  const handleClick = useCallback(() => {
    onClick(item.id);
  }, [onClick, item.id]);

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-label={item.label}
      className={clsx(styles.tab, isActive && styles.tabActive)}
      onClick={handleClick}
    >
      <TabContent item={item} />
    </button>
  );
});

export const CollapsibleTabs = memo(function CollapsibleTabs({
  items,
  activeItem,
  onItemChange,
  className,
}: CollapsibleTabsProps) {
  const { rootRef, measureRef, expandAll } = useFitsExpanded(items);

  return (
    <div ref={rootRef} className={styles.root}>
      <div
        role="tablist"
        className={clsx(
          styles.tabList,
          expandAll && styles.tabListExpanded,
          className
        )}
      >
        {items.map((item) => (
          <TabButton
            key={item.id}
            item={item}
            isActive={item.id === activeItem}
            onClick={onItemChange}
          />
        ))}
      </div>
      {/* Hidden twin — always fully expanded, measured to decide `expandAll`. */}
      <div ref={measureRef} className={styles.measure} aria-hidden>
        {items.map((item) => (
          <div key={item.id} className={styles.tab}>
            <TabContent item={item} />
          </div>
        ))}
      </div>
    </div>
  );
});
